import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { UserProvider, useUser } from './context/UserContext';
import { DarkModeProvider } from './context/DarkModeContext';
import Navbar from './components/Navbar';
import ChatNotification from './components/ChatNotification';
import Home from './pages/Home';
import LiveDebate from './pages/LiveDebate';
import LiveDebateRoom from './pages/LiveDebateRoom';
import Debate from './pages/Debate';
import Training from './pages/Training';
import TrainingModule from './pages/TrainingModule';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import Tournament from './pages/Tournament';
import TournamentRegistration from './pages/TournamentRegistration';
import Chat from './pages/Chat';
import Feedback from './pages/Feedback';
import Friends from './pages/Friends';
import UserProfile from './pages/UserProfile';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import GoogleOAuthTest from './pages/GoogleOAuthTest';
import Onboarding from './pages/Onboarding';
import ReportUser from './pages/ReportUser';
import Admin from './pages/Admin';
import TournamentComingSoon from './pages/TournamentComingSoon';

// ScrollToTop component to handle automatic scrolling on route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top with smooth behavior
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// Protected Route Component - only shows loading when checking authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useUser();
  
  // If we're still loading and there's a token, show loading
  if (loading && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 scale-container">
        <div className="text-center responsive-padding">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300 scale-text">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: { pathname: window.location.pathname, search: window.location.search } }} replace />;
  }
  
  return children;
};

// Public Route Component (redirects authenticated users)
const PublicRoute = ({ children }) => {
  const { user, loading } = useUser();
  
  // If we're still loading and there's a token, show loading
  if (loading && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 scale-container">
        <div className="text-center responsive-padding">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300 scale-text">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If authenticated, redirect to home
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 scale-container">
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 safe-top responsive-padding">
          <ChatNotification />
          <Routes>
            {/* Public Routes - No loading state for unauthenticated users */}
            <Route path="/" element={<Home />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/feedback" element={<Feedback />} />
            
            {/* Authentication Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            } />
            <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        <Route path="/test/google-oauth" element={<GoogleOAuthTest />} />
            <Route path="/onboarding" element={<Onboarding />} />
            
            {/* Protected Routes */}
            <Route path="/live-debate" element={
              <ProtectedRoute>
                <Debate />
              </ProtectedRoute>
            } />

            <Route path="/training" element={
              <ProtectedRoute>
                <Training />
              </ProtectedRoute>
            } />
            <Route path="/training/module/:moduleId" element={
              <ProtectedRoute>
                <TrainingModule />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/profile/edit" element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            } />
            <Route path="/tournament" element={
              <ProtectedRoute>
                <TournamentComingSoon />
              </ProtectedRoute>
            } />
            <Route path="/tournament/register/:tournamentId" element={
              <ProtectedRoute>
                <TournamentComingSoon />
              </ProtectedRoute>
            } />
            <Route path="/friends" element={
              <ProtectedRoute>
                <Friends />
              </ProtectedRoute>
            } />
            <Route path="/chat" element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            } />
            <Route path="/chat/:chatId" element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            } />
            <Route path="/profile/:userId" element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            } />
            <Route path="/report-user/:userId" element={
              <ProtectedRoute>
                <ReportUser />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            } />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function App() {
  return (
    <DarkModeProvider>
      <UserProvider>
        <AppRoutes />
      </UserProvider>
    </DarkModeProvider>
  );
}

export default App; 